/**
 * Customer Detail — Light + Accent Stripe Design
 * Profile header, quota progress bars, usage history, admin actions
 */
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { CircularProgress, Radio, RadioGroup } from '@mui/joy'
import { ArrowBack, CreditCard, Refresh, DataUsage, History, Person, Tag, PhoneIphone, LocalShipping } from '@mui/icons-material'
import { motion } from 'framer-motion'
import { FeatureKey } from '@numeo/types-core'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { billingApi } from './api'
import { CustomerDetail as CustomerDetailType, UsageHistoryRow, CancelMode } from './types'
import QuotaHealthCheck from './QuotaHealthCheck'
import AdminActionDialog from './AdminActionDialog'
import ExtensionLanePanel from './ExtensionLanePanel'
import MobileLanePanel from './MobileLanePanel'
import TmsLanePanel from './TmsLanePanel'

dayjs.extend(relativeTime)

const ease = [0.23, 1, 0.32, 1]

type LaneTab = 'extension' | 'mobile' | 'tms'

const LANE_TABS: { key: LaneTab; label: string; Icon: typeof CreditCard }[] = [
    { key: 'extension', label: 'Extension', Icon: CreditCard },
    { key: 'mobile', label: 'Mobile', Icon: PhoneIphone },
    { key: 'tms', label: 'TMS', Icon: LocalShipping },
]

function getInitials(email: string): string {
    if (!email || email === 'N/A') return '?'
    const parts = email.split('@')[0].split(/[._-]/)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return email.substring(0, 2).toUpperCase()
}

function getQuotaColor(remaining: number, limit: number): { bar: string; text: string; bg: string } {
    const pct = limit > 0 ? (remaining / limit) * 100 : 100
    if (pct > 50) return { bar: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50' }
    if (pct > 20) return { bar: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50' }
    return { bar: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50' }
}

export default function CustomerDetailPage() {
    const { appId } = useParams<{ appId: string }>()
    const navigate = useNavigate()
    const [detail, setDetail] = useState<CustomerDetailType | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [usageRows, setUsageRows] = useState<UsageHistoryRow[]>([])
    const [usageTotal, setUsageTotal] = useState(0)
    const [usagePage, setUsagePage] = useState(1)
    const [usageTotalPages, setUsageTotalPages] = useState(0)
    const [usageLoading, setUsageLoading] = useState(false)
    const [usageFeatureFilter, setUsageFeatureFilter] = useState('')
    const [usageFromDate, setUsageFromDate] = useState('')
    const [usageToDate, setUsageToDate] = useState('')
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [resetDialogOpen, setResetDialogOpen] = useState(false)
    const [cancelMode, setCancelMode] = useState<CancelMode>('at_period_end')
    const [resetQuotaFeature, setResetQuotaFeature] = useState<string | undefined>(undefined)
    const [actionLoading, setActionLoading] = useState(false)
    const [actionSuccess, setActionSuccess] = useState<string | null>(null)
    const [actionError, setActionError] = useState<string | null>(null)
    const [laneTab, setLaneTab] = useState<LaneTab>('extension')

    const refreshDetail = useCallback(async () => {
        if (!appId) return
        try {
            const data = await billingApi.getCustomerDetail(appId)
            setDetail(data)
        } catch {
            // Best-effort refresh — keep showing the last good detail if the refetch fails.
        }
    }, [appId])

    const handleCancelSubscription = useCallback(
        async (reason: string) => {
            if (!appId) return
            try {
                setActionLoading(true)
                await billingApi.cancelSubscription(appId, { mode: cancelMode, reason })
                setCancelDialogOpen(false)
                setActionSuccess(`Subscription ${cancelMode === 'immediately' ? 'cancelled immediately' : 'set to cancel at period end'}`)
                await refreshDetail()
            } catch (err: unknown) {
                const axiosErr = err as { response?: { data?: { message?: string } } }
                setActionError(axiosErr.response?.data?.message || 'Failed to cancel subscription')
            } finally {
                setActionLoading(false)
            }
        },
        [appId, cancelMode, refreshDetail]
    )

    const handleDeleteStripeCustomer = useCallback(
        async (reason: string) => {
            if (!appId) return
            try {
                setActionLoading(true)
                await billingApi.deleteStripeCustomer(appId, { reason })
                setDeleteDialogOpen(false)
                setActionSuccess('Stripe customer deleted successfully')
                await refreshDetail()
            } catch (err: unknown) {
                const axiosErr = err as { response?: { data?: { message?: string } } }
                setActionError(axiosErr.response?.data?.message || 'Failed to delete Stripe customer')
            } finally {
                setActionLoading(false)
            }
        },
        [appId, refreshDetail]
    )

    const handleResetQuota = useCallback(
        async (reason: string) => {
            if (!appId) return
            try {
                setActionLoading(true)
                await billingApi.resetQuota(appId, { featureKey: resetQuotaFeature as FeatureKey | undefined, reason })
                setResetDialogOpen(false)
                setActionSuccess(resetQuotaFeature ? `Quota for "${resetQuotaFeature}" reset` : 'All quotas reset')
                await refreshDetail()
            } catch (err: unknown) {
                const axiosErr = err as { response?: { data?: { message?: string } } }
                setActionError(axiosErr.response?.data?.message || 'Failed to reset quota')
            } finally {
                setActionLoading(false)
            }
        },
        [appId, resetQuotaFeature, refreshDetail]
    )

    useEffect(() => {
        if (actionSuccess) {
            const t = setTimeout(() => setActionSuccess(null), 5000)
            return () => clearTimeout(t)
        }
    }, [actionSuccess])
    useEffect(() => {
        if (actionError) {
            const t = setTimeout(() => setActionError(null), 5000)
            return () => clearTimeout(t)
        }
    }, [actionError])

    useEffect(() => {
        if (!appId) return
        setLoading(true)
        billingApi
            .getCustomerDetail(appId)
            .then(setDetail)
            .catch((err: { response?: { data?: { message?: string } } }) => setError(err.response?.data?.message || 'Failed to fetch customer'))
            .finally(() => setLoading(false))
    }, [appId])

    const fetchUsageHistory = useCallback(
        async (p: number = 1) => {
            if (!appId) return
            try {
                setUsageLoading(true)
                const data = await billingApi.getUsageHistory(appId, {
                    featureKey: (usageFeatureFilter || undefined) as FeatureKey | undefined,
                    fromDate: usageFromDate || undefined,
                    toDate: usageToDate || undefined,
                    page: p,
                    pageSize: 25,
                })
                setUsageRows(data.rows)
                setUsageTotal(data.total)
                setUsagePage(p)
                setUsageTotalPages(Math.ceil(data.total / 25))
            } catch {
                // Non-blocking table load — leave the previous rows in place on failure.
            } finally {
                setUsageLoading(false)
            }
        },
        [appId, usageFeatureFilter, usageFromDate, usageToDate]
    )

    // Reload the usage table when the customer changes; filter edits reload it from the Filter button. The effect
    // calls the latest fetchUsageHistory through a ref instead of depending on it.
    const fetchUsageHistoryRef = useRef(fetchUsageHistory)
    useEffect(() => {
        fetchUsageHistoryRef.current = fetchUsageHistory
    }, [fetchUsageHistory])
    useEffect(() => {
        fetchUsageHistoryRef.current(1)
    }, [appId])

    if (loading)
        return (
            <div className="flex justify-center py-20">
                <CircularProgress />
            </div>
        )
    if (error || !detail)
        return (
            <div className="p-6">
                <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{error || 'Customer not found'}</div>
            </div>
        )

    const sub = detail.subscription
    const HIDDEN_FEATURES = ['aiCall', 'aiEmail']
    const usageEntries = Object.entries(detail.usage || {}).filter(([key]) => !HIDDEN_FEATURES.includes(key))

    return (
        <div className="p-6 w-full">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-5">
                <span className="hover:text-gray-600 cursor-pointer" onClick={() => navigate('/billing')}>
                    Home
                </span>
                <span>/</span>
                <span className="hover:text-gray-600 cursor-pointer" onClick={() => navigate('/billing')}>
                    Billing
                </span>
                <span>/</span>
                <span className="text-gray-600 font-medium">{detail.projectName}</span>
            </div>

            {/* Back */}
            <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.2, ease }}>
                <button
                    onClick={() => navigate('/billing')}
                    className="mb-4 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors -ml-2"
                >
                    <ArrowBack sx={{ fontSize: 15 }} /> Back to Customers
                </button>
            </motion.div>

            {/* Alerts */}
            {actionSuccess && (
                <div className="bg-emerald-50 text-emerald-700 px-4 py-2 rounded-lg text-sm mb-3 flex items-center justify-between">
                    {actionSuccess}
                    <button onClick={() => setActionSuccess(null)} className="text-xs hover:underline">
                        Dismiss
                    </button>
                </div>
            )}
            {actionError && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm mb-3 flex items-center justify-between">
                    {actionError}
                    <button onClick={() => setActionError(null)} className="text-xs hover:underline">
                        Dismiss
                    </button>
                </div>
            )}

            {/* Customer Header */}
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, ease }}>
                <div className="mb-6 rounded-xl overflow-hidden shadow-sm ring-1 ring-gray-200">
                    <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-6 py-5">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center text-white font-bold text-sm backdrop-blur-sm">
                                    {getInitials(detail.ownerEmail)}
                                </div>
                                <div>
                                    <h2 className="text-white font-bold text-lg">{detail.projectName}</h2>
                                    <div className="flex items-center gap-3 mt-1">
                                        <span className="text-slate-300 text-xs flex items-center gap-1">
                                            <Tag sx={{ fontSize: 10 }} /> {detail.applicationId}
                                        </span>
                                        <span className="text-slate-300 text-xs flex items-center gap-1">
                                            <Person sx={{ fontSize: 10 }} /> {detail.ownerEmail}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            {(detail.effective || sub) &&
                                (() => {
                                    // Headline = the customer's EFFECTIVE plan (best-of personal / team seat /
                                    // app lane) — never the raw app-lane sub, which can be a stale signup trial.
                                    const eff = detail.effective ?? { plan: sub!.plan, status: sub!.status, source: 'APP' as const }
                                    return (
                                        <div className="flex items-center gap-2">
                                            {eff.source === 'TEAM_GRANT' && (
                                                <span className="text-[10px] text-slate-300 uppercase tracking-wider">via team seat</span>
                                            )}
                                            {eff.source === 'PERSONAL' && (
                                                <span className="text-[10px] text-slate-300 uppercase tracking-wider">personal plan</span>
                                            )}
                                            <span
                                                className={`text-xs font-bold px-3 py-1 rounded border ${eff.plan === 'PRO' ? 'bg-blue-500/20 text-blue-200 border-blue-400/30' : eff.plan === 'ULTRA' ? 'bg-violet-500/20 text-violet-200 border-violet-400/30' : 'bg-gray-500/20 text-gray-200 border-gray-400/30'}`}
                                            >
                                                {eff.plan}
                                            </span>
                                            <span
                                                className={`text-xs px-3 py-1 rounded border ${eff.status === 'active' ? 'bg-emerald-500/20 text-emerald-200 border-emerald-400/30' : eff.status === 'trialing' ? 'bg-amber-500/20 text-amber-200 border-amber-400/30' : 'bg-red-500/20 text-red-200 border-red-400/30'}`}
                                            >
                                                {eff.status}
                                            </span>
                                        </div>
                                    )
                                })()}
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Billing lanes — Extension | Mobile | TMS */}
            {appId && (
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1, ease }}>
                    <div className="inline-flex items-center bg-gray-100 rounded-lg p-1 gap-1 mb-4">
                        {LANE_TABS.map(({ key, label, Icon }) => (
                            <button
                                key={key}
                                onClick={() => setLaneTab(key)}
                                className={`h-8 px-4 text-xs font-semibold rounded-md flex items-center gap-1.5 transition-all ${laneTab === key ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                <Icon sx={{ fontSize: 14 }} /> {label}
                            </button>
                        ))}
                    </div>
                    {laneTab === 'extension' && (
                        <ExtensionLanePanel
                            appId={appId}
                            detail={detail}
                            onOpenCancelDialog={() => setCancelDialogOpen(true)}
                            onOpenDeleteDialog={() => setDeleteDialogOpen(true)}
                            onSuccess={setActionSuccess}
                            onRefresh={refreshDetail}
                        />
                    )}
                    {laneTab === 'mobile' && (
                        <MobileLanePanel
                            appId={appId}
                            users={detail.users || []}
                            onSuccess={setActionSuccess}
                            onError={setActionError}
                            onRefresh={refreshDetail}
                        />
                    )}
                    {laneTab === 'tms' && (
                        <TmsLanePanel
                            appId={appId}
                            tmsSubscription={detail.tmsSubscription}
                            onSuccess={setActionSuccess}
                            onError={setActionError}
                            onRefresh={refreshDetail}
                        />
                    )}
                </motion.div>
            )}

            {/* Current Quota Usage */}
            {usageEntries.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.2, ease }}>
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-6">
                        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-md bg-indigo-50 flex items-center justify-center">
                                    <DataUsage sx={{ fontSize: 14 }} className="text-indigo-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-gray-900">Current Quota Usage</p>
                                    <p className="text-xs text-gray-400">Resource consumption for the current billing period</p>
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setResetQuotaFeature(undefined)
                                    setResetDialogOpen(true)
                                }}
                                className="h-8 px-3 text-xs font-medium border border-gray-200 rounded-lg hover:bg-orange-50 hover:text-orange-700 hover:border-orange-200 transition-colors flex items-center gap-1.5"
                            >
                                <Refresh sx={{ fontSize: 12 }} /> Reset All
                            </button>
                        </div>
                        <div className="p-5">
                            <div className="grid grid-cols-2 gap-3 mb-4">
                                {usageEntries.map(([key, u], idx) => {
                                    const used = u.limit - u.remaining
                                    const usedPct = u.limit > 0 ? (used / u.limit) * 100 : 0
                                    const colors = getQuotaColor(u.remaining, u.limit)
                                    return (
                                        <motion.div
                                            key={key}
                                            initial={{ opacity: 0, scale: 0.95 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ duration: 0.2, delay: 0.25 + idx * 0.05, ease }}
                                            className="bg-gray-50 rounded-lg p-3.5 border border-gray-100"
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <p className="text-sm font-medium text-gray-800 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</p>
                                                <button
                                                    onClick={() => {
                                                        setResetQuotaFeature(key)
                                                        setResetDialogOpen(true)
                                                    }}
                                                    className="w-6 h-6 rounded text-gray-400 hover:text-indigo-600 inline-flex items-center justify-center"
                                                >
                                                    <Refresh sx={{ fontSize: 11 }} />
                                                </button>
                                            </div>
                                            <div className="flex items-center gap-3 mb-1.5">
                                                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                                    <motion.div
                                                        initial={{ width: 0 }}
                                                        animate={{ width: `${usedPct}%` }}
                                                        transition={{ duration: 0.6, delay: 0.3 + idx * 0.05, ease }}
                                                        className={`h-full rounded-full ${colors.bar}`}
                                                    />
                                                </div>
                                                <span className={`text-xs font-bold ${colors.text}`}>{Math.round(usedPct)}%</span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-[10px] text-gray-400">
                                                    {u.remaining} / {u.limit} remaining
                                                </span>
                                                <span className="text-[10px] text-gray-400">
                                                    {dayjs(u.periodStart).format('MMM D')} — {dayjs(u.periodEnd).format('MMM D')}
                                                </span>
                                            </div>
                                        </motion.div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Quota Health Check */}
            {appId && (
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.3, ease }}>
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-6">
                        <QuotaHealthCheck appId={appId} />
                    </div>
                </motion.div>
            )}

            {/* Usage History */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.4, ease }}>
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
                    <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                        <History sx={{ fontSize: 16 }} className="text-gray-500" />
                        <p className="text-sm font-semibold text-gray-900">Usage History</p>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">{usageTotal}</span>
                    </div>
                    <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50 flex gap-2 items-end">
                        <input
                            placeholder="Feature (e.g. aiEmail)"
                            value={usageFeatureFilter}
                            onChange={(e) => setUsageFeatureFilter(e.target.value)}
                            className="h-8 text-sm px-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 w-40"
                        />
                        <input
                            type="date"
                            value={usageFromDate}
                            onChange={(e) => setUsageFromDate(e.target.value)}
                            className="h-8 text-sm px-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 w-36"
                        />
                        <input
                            type="date"
                            value={usageToDate}
                            onChange={(e) => setUsageToDate(e.target.value)}
                            className="h-8 text-sm px-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 w-36"
                        />
                        <button
                            onClick={() => fetchUsageHistory(1)}
                            disabled={usageLoading}
                            className="h-8 px-4 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                        >
                            {usageLoading ? <CircularProgress size="sm" sx={{ '--CircularProgress-size': '12px' }} /> : 'Filter'}
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50/80 text-left">
                                    <th className="px-5 py-3 text-xs font-semibold text-gray-600">Feature</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Timestamp</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600 text-center">Remaining</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600 text-center">Limit</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">User ID</th>
                                </tr>
                            </thead>
                            <tbody>
                                {usageLoading ? (
                                    <tr>
                                        <td colSpan={5} className="text-center py-12">
                                            <CircularProgress size="sm" />
                                        </td>
                                    </tr>
                                ) : usageRows.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="text-center py-12 text-gray-400 text-sm">
                                            No usage history found
                                        </td>
                                    </tr>
                                ) : (
                                    usageRows.map((row) => (
                                        <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
                                            <td className="px-5 py-2.5 font-medium text-gray-800">{row.featureKey}</td>
                                            <td className="px-2 py-2.5 text-gray-500 text-xs">{dayjs(row.usageTimestamp).format('MMM D, HH:mm:ss')}</td>
                                            <td className="px-2 py-2.5 text-center">{row.quotaRemainingAfter}</td>
                                            <td className="px-2 py-2.5 text-center">{row.quotaLimit}</td>
                                            <td className="px-2 py-2.5 font-mono text-[11px] text-gray-500">{row.userId}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    {usageTotalPages > 1 && (
                        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
                            <p className="text-xs text-gray-500">
                                Page {usagePage} of {usageTotalPages} ({usageTotal} total)
                            </p>
                            <div className="flex items-center gap-2">
                                <button
                                    disabled={usagePage <= 1 || usageLoading}
                                    onClick={() => fetchUsageHistory(usagePage - 1)}
                                    className="px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Previous
                                </button>
                                <button
                                    disabled={usagePage >= usageTotalPages || usageLoading}
                                    onClick={() => fetchUsageHistory(usagePage + 1)}
                                    className="px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </motion.div>

            {/* Dialogs */}
            <AdminActionDialog
                open={cancelDialogOpen}
                title="Cancel Subscription"
                description="Choose whether to cancel immediately or at the end of the current billing period."
                severity="warning"
                confirmLabel="Cancel Subscription"
                loading={actionLoading}
                onConfirm={handleCancelSubscription}
                onClose={() => setCancelDialogOpen(false)}
            >
                <RadioGroup value={cancelMode} onChange={(e) => setCancelMode(e.target.value as CancelMode)} sx={{ mt: 1 }}>
                    <Radio value="at_period_end" label="Cancel at period end (recommended)" />
                    <Radio value="immediately" label="Cancel immediately (downgrades to FREE now)" />
                </RadioGroup>
            </AdminActionDialog>
            <AdminActionDialog
                open={deleteDialogOpen}
                title="Delete Stripe Customer"
                description="This will permanently delete the Stripe customer record."
                severity="danger"
                requireTyping
                confirmationText="DELETE CUSTOMER"
                confirmLabel="Delete Customer"
                loading={actionLoading}
                onConfirm={handleDeleteStripeCustomer}
                onClose={() => setDeleteDialogOpen(false)}
            />
            <AdminActionDialog
                open={resetDialogOpen}
                title={resetQuotaFeature ? `Reset Quota: ${resetQuotaFeature}` : 'Reset All Quotas'}
                description={resetQuotaFeature ? `Reset quota for "${resetQuotaFeature}" to its full limit.` : 'Reset all quota counters to their full limits.'}
                severity="warning"
                confirmLabel="Reset Quota"
                loading={actionLoading}
                onConfirm={handleResetQuota}
                onClose={() => setResetDialogOpen(false)}
            />
        </div>
    )
}
